import { memo } from 'react';

import {
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
  DATE_FORMAT_LONG_MINUTE_SHORT_FNS,
  SYSTEM_DATE_FORMAT_SHORT_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { IKnmPreview } from 'APIPackage/KnmSearchModule/interfaces/IKnmPreview';
import { getApproveRequiredSelectOptionsByCreated } from 'KnmErknmPackage/MainModule/constants/approveRequiredSelectOptions';
import { formatDate, formatDateLocale } from 'CorePackage/MainModule/common/formatDate';

import styles from './KnmPreview.module.scss';
import { KnmSubtitle } from './KnmSubtitle';
import { convertArrayToDate } from '../common/convertArrayToDate';
import { parseErknmValue } from '../common/parseErknmValue';
import { SubjectNotifications } from './SubjectNotifications';
import { EpguNotifications } from './EpguNotifications';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { convertQuillDeltaToHtml } from '../../../functions/convertQuillDeltaToHtml';
import { IReactQuillDelta } from '../../../APIPackage/KnmModule/interfaces/IReactQuillDelta';

interface IErknmCommonProps {
  passportData: IKnmPreview;
  settings?: IAdminSetting[];
}

export const ErknmCommon = memo(({ passportData, settings }: IErknmCommonProps) => {
  const renderFzName = (fzName: string) => {
    const splitName = fzName.split(' ');

    if (splitName.length > 1) {
      return splitName.join('-');
    }

    return `${fzName}-ФЗ`;
  };

  return (
    <div className={styles.ColumnContent}>
      <KnmSubtitle title="Общий состав атрибутов паспорта КНМ">
        {passportData.erpId && (
          <div className={styles.Row}>
            <div className={styles.ColText}>
              Учетный номер КНМ в соответствии с постановлением Правительства РФ от 28 апреля 2015 г. N 415 &quot;О
              Правилах формирования и ведения единого реестра проверок&quot;
            </div>
            <div className={styles.ColValue}>{passportData.erpId}</div>
          </div>
        )}
        {passportData.typeName && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Характер КНМ</div>
            <div className={styles.ColValue}>{passportData.typeName}</div>
          </div>
        )}
        <div className={styles.Row}>
          <div className={styles.ColText}>Федеральный закон, в соответствии с которым проводится КНМ</div>
          <div className={styles.ColValue}>{passportData.fzName && renderFzName(passportData.fzName)}</div>
        </div>
        {passportData.status && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Статус КНМ</div>
            <div className={styles.ColValue}>{passportData.status}</div>
          </div>
        )}
        {passportData.created && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата регистрации в ФГИС ЕРП</div>
            <div className={styles.ColValue}>
              {formatDateLocale({
                date: convertArrayToDate(passportData?.created),
                dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
                parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
              })}
            </div>
          </div>
        )}
        {passportData.approve && passportData.approve.impossibleConductionReason && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Сведения о причинах невозможности проведения КНМ</div>
            <div className={styles.ColValue}>{passportData.approve.impossibleConductionReason}</div>
          </div>
        )}
        {passportData.inspectionErknm?.kindKnm && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Вид КНМ</div>
            <div className={styles.ColValue}>{parseErknmValue(passportData.inspectionErknm.kindKnm.value)?.title}</div>
          </div>
        )}
      </KnmSubtitle>
      {passportData.inspectionErknm?.decision && (
        <>
          <KnmSubtitle title="Решение о проведении КНМ">
            {passportData.inspectionErknm.decision.dateTimeDecision && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Дата и время издания решения</div>
                <div className={styles.ColValue}>
                  {formatDate({
                    date: convertArrayToDate(passportData?.inspectionErknm?.decision?.dateTimeDecision),
                    dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
                    parseFormat: DATE_FORMAT_LONG_MINUTE_SHORT_FNS,
                  })}
                </div>
              </div>
            )}
            {passportData.inspectionErknm.decision.numberDecision && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Номер решения</div>
                <div className={styles.ColValue}>{passportData.inspectionErknm.decision.numberDecision}</div>
              </div>
            )}
            {passportData.inspectionErknm.decision.placeDecision && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Место вынесения решения</div>
                <div className={styles.ColValue}>{passportData.inspectionErknm.decision.placeDecision}</div>
              </div>
            )}
            {passportData.inspectionErknm.decision.fioSigner && (
              <div className={styles.Row}>
                <div className={styles.ColText}>ФИО должностного лица</div>
                <div className={styles.ColValue}>{passportData.inspectionErknm.decision.fioSigner}</div>
              </div>
            )}
            {passportData.inspectionErknm.decision.titleSigner && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Должность лица, подписавшего решение</div>
                <div className={styles.ColValue}>
                  {parseErknmValue(passportData.inspectionErknm.decision.titleSigner.value)?.title}
                </div>
              </div>
            )}
          </KnmSubtitle>
        </>
      )}
      <KnmSubtitle title="Даты проведения КНМ">
        {passportData.startDate && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата начала КНМ</div>
            <div className={styles.ColValue}>
              {formatDateLocale({
                date: convertArrayToDate(passportData?.startDate),
                dateFormat: DATE_FORMAT_LONG_FNS,
                parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
              })}
            </div>
          </div>
        )}
        {passportData.stopDate && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата окончания КНМ</div>
            <div className={styles.ColValue}>
              {formatDateLocale({
                date: convertArrayToDate(passportData?.stopDate),
                dateFormat: DATE_FORMAT_LONG_FNS,
                parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
              })}
            </div>
          </div>
        )}
      </KnmSubtitle>
      {passportData.inspectionErknm?.reasons && passportData.inspectionErknm.reasons.length > 0 && (
        <KnmSubtitle title="Перечень основания проведения КНМ">
          {passportData.inspectionErknm.reasons.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>Основание проведения КНМ</span>
              {item.reason && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Наименование</div>
                  <div className={styles.ColValue}>
                    {item.reason.ervkUuid ? item.reason.title : item.reason.type.name}
                  </div>
                </div>
              )}
              {item.reason.date && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата</div>
                  <div className={styles.ColValue}>
                    {formatDateLocale({
                      date: convertArrayToDate(item.reason.date),
                      dateFormat: DATE_FORMAT_LONG_FNS,
                      parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
                    })}
                  </div>
                </div>
              )}
              {item.reason.requirementDetails && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Реквизиты требования</div>
                  <div className={styles.ColValue}>{item.reason.requirementDetails}</div>
                </div>
              )}
              {item.reason.assignmentNumber && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Номер поручения</div>
                  <div className={styles.ColValue}>{item.reason.assignmentNumber}</div>
                </div>
              )}
              {item.reason.assignmentDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата поручения</div>
                  <div className={styles.ColValue}>
                    {formatDateLocale({
                      date: convertArrayToDate(item.reason.assignmentDate),
                      dateFormat: DATE_FORMAT_LONG_FNS,
                      parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
                    })}
                  </div>
                </div>
              )}
              {item.riskIndicators?.map((indicatorValue, indicatorIndex) => {
                return (
                  <>
                    {indicatorValue.riskIndicator && (
                      <div key={indicatorIndex}>
                        <span className={styles.SectionTitle}>Индикатор риска #{indicatorIndex + 1}</span>
                        <div className={styles.Row}>
                          <div className={styles.ColText}>
                            {parseErknmValue(indicatorValue.riskIndicator.value)?.title}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                );
              })}
            </div>
          ))}
        </KnmSubtitle>
      )}
      {passportData.inspectionErknm?.approve?.approveRequired && (
        <KnmSubtitle title="Необходимость согласования">
          <div className={styles.Row}>
            <div className={styles.ColText}>Необходимость согласования</div>
            <div className={styles.ColValue}>
              {
                getApproveRequiredSelectOptionsByCreated(convertArrayToDate(passportData.created), settings).find(
                  item => item.id === passportData?.inspectionErknm?.approve?.approveRequired,
                )?.label
              }
            </div>
          </div>
          {passportData.inspectionErknm.dataContent && (
            <div className={styles.Row}>
              <div className={styles.ColText}>
                Сведения о причинении вреда (ущерба) или об угрозе причинения вреда (ущерба) охраняемым законом
                ценностям в случаях отсутствия необходимости согласования проведения контрольного (надзорного)
                мероприятия с органами прокуратуры в соответствии со статьей 66 Федерального закона
              </div>
              <div className={styles.ColValue}>{passportData.inspectionErknm.dataContent}</div>
            </div>
          )}
        </KnmSubtitle>
      )}
      <KnmSubtitle title="Сведения о согласовании проведения КНМ с органами прокуратуры">
        {passportData.prosecName && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Наименование прокуратуры</div>
            <div className={styles.ColValue}>{passportData.prosecName}</div>
          </div>
        )}
        {passportData.inspectionErknm?.approve?.approved && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Решение по заявлению</div>
            <div className={styles.ColValue}>{passportData.inspectionErknm.approve.approved}</div>
          </div>
        )}
        {passportData.inspectionErknm?.approve?.rejectReasonType &&
          passportData.inspectionErknm?.approve?.rejectReasonType.name && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Причина отказа</div>
              <div className={styles.ColValue}>{passportData.inspectionErknm.approve.dateRejectReason}</div>
            </div>
          )}
        {passportData.inspectionErknm?.approve?.numberDecision && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Номер решения</div>
            <div className={styles.ColValue}>{passportData.inspectionErknm.approve.numberDecision}</div>
          </div>
        )}
        {passportData.inspectionErknm?.approve?.dateDecision && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата решения</div>
            <div className={styles.ColValue}>
              {formatDateLocale({
                date: convertArrayToDate(passportData.inspectionErknm.approve.dateDecision),
                dateFormat: DATE_FORMAT_LONG_FNS,
                parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
              })}
            </div>
          </div>
        )}
        {passportData.inspectionErknm?.approve?.fioSigner && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Кем принято решение</div>
            <div className={styles.ColValue}>{passportData.inspectionErknm.approve.fioSigner}</div>
          </div>
        )}
        {passportData.inspectionErknm?.approve?.titleSigner && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Должность лица, подписавшего решение</div>
            <div className={styles.ColValue}>{passportData.inspectionErknm.approve.titleSigner}</div>
          </div>
        )}
      </KnmSubtitle>
      <KnmSubtitle title="Предмет контрольного (надзорного) мероприятия">
        {passportData.inspectionErknm?.requirementsNew && passportData.inspectionErknm.requirementsNew.length > 0 && (
          <KnmSubtitle title="Обязательные требования, подлежащие проверке">
            {passportData?.inspectionErknm.requirementsNew.map((npa, index) => (
              <div key={index}>
                <span className={styles.SectionTitle}>Обязательные требования, подлежащие проверке #{index + 1}</span>
                <div className={styles.Row}>
                  <div className={styles.ColText}>Нормативно-правовой акт</div>
                  <div className={styles.ColValue}>{npa.title}</div>
                </div>
                <div className={styles.Row}>
                  <div className={styles.ColText}>Номер</div>
                  <div className={styles.ColValue}>{npa.number}</div>
                </div>
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата утверждения</div>
                  <div className={styles.ColValue}>
                    {formatDateLocale({
                      date: convertArrayToDate(passportData.inspectionErknm?.approve?.dateDecision),
                      dateFormat: DATE_FORMAT_LONG_FNS,
                      parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
                    })}
                  </div>
                </div>
                <div className={styles.Row}>
                  <div className={styles.ColText}>Структурная единица</div>
                  <div className={styles.ColValue}>{npa.structuralUnit.title}</div>
                </div>
              </div>
            ))}
          </KnmSubtitle>
        )}
        {passportData.inspectionErknm?.approveDocs && passportData.inspectionErknm.approveDocs.length > 0 && (
          <KnmSubtitle title="Разрешительные и иные документы">
            {passportData.inspectionErknm.approveDocs.map((item, index) => (
              <div className={styles.SectionBlock} key={index}>
                {item.approveDoc && (
                  <>
                    <span className={styles.SectionTitle}>Документ #{index + 1}</span>
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Наименование документа</div>
                      <div className={styles.ColValue}>{parseErknmValue(item.approveDoc.value)?.title}</div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </KnmSubtitle>
        )}
        <KnmSubtitle title="Сведения о применяемом проверочном листе">
          {passportData.inspectionErknm?.isChecklistsUsed !== null && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Отметка об использовании проверочного листа</div>
              <div className={styles.ColValue}>{passportData.inspectionErknm?.isChecklistsUsed ? 'Да' : 'Нет'}</div>
            </div>
          )}
          {passportData.inspectionErknm?.checklists &&
            passportData.inspectionErknm.checklists.length > 0 &&
            passportData.inspectionErknm.checklists.map((item, index) => {
              const checkList = item.checkList && parseErknmValue(item.checkList.value);

              return (
                <div className={styles.SectionBlock} key={index}>
                  {checkList && (
                    <>
                      <span className={styles.SectionTitle}>Проверочный лист #{index + 1}</span>
                      <div className={styles.Row}>
                        <div className={styles.ColText}>Наименование проверочного листа</div>
                        <div className={styles.ColValue}>{checkList.title}</div>
                      </div>
                      {checkList.questions && checkList.questions.length > 0 && (
                        <KnmSubtitle title="Контрольные вопросы">
                          {checkList.questions.map((question, questionIndex) => (
                            <div key={questionIndex}>
                              <span className={styles.SectionTitle}>Контрольный вопрос #{questionIndex + 1}</span>
                              <div className={styles.Row}>
                                <div className={styles.ColText}>Вопрос</div>
                                <div className={styles.ColValue}>{question.title}</div>
                              </div>
                            </div>
                          ))}
                        </KnmSubtitle>
                      )}
                    </>
                  )}
                </div>
              );
            })}
        </KnmSubtitle>
        {passportData.inspectionErknm?.places && passportData.inspectionErknm.places.length > 0 && (
          <KnmSubtitle title="Место (места) проведения контрольного (надзорного) мероприятия">
            {passportData.inspectionErknm.places.map((item, index) => (
              <div key={index}>
                {item.value && (
                  <>
                    <span className={styles.SectionTitle}>Место #{index + 1}</span>
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Адрес</div>
                      <div className={styles.ColValue}>{item.value}</div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </KnmSubtitle>
        )}
        {passportData.inspectionErknm?.organizationDocuments &&
          passportData.inspectionErknm.organizationDocuments.length > 0 && (
            <KnmSubtitle title="Перечень представленных контролируемыми лицами документов">
              {passportData.inspectionErknm.organizationDocuments.map((item, index) => (
                <div key={index}>
                  {item.value && (
                    <>
                      <span className={styles.SectionTitle}>Документ #{index + 1}</span>
                      <div className={styles.Row}>
                        <div className={styles.ColText}>Наименование</div>
                        <div className={styles.ColValue}>{item.value}</div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </KnmSubtitle>
          )}
      </KnmSubtitle>
      <SubjectNotifications subjectNotifications={passportData?.subjectNotifications} />
      <EpguNotifications epguNotifications={passportData?.epguNotifications} />
      {(passportData.inspectionErknm?.isPresence !== null ||
        passportData.inspectionErknm.isPresenceDelegateFullNames) && (
        <KnmSubtitle title="Отметка о присутствии контролируемого лица и (или) его представителя)">
          {passportData.inspectionErknm?.isPresence !== null && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Отметка</div>
              <div className={styles.ColValue}>{passportData.inspectionErknm?.isPresence ? 'Да' : 'Нет'}</div>
            </div>
          )}
          {passportData.inspectionErknm?.isPresenceDelegateFullNames &&
            passportData.inspectionErknm.isPresenceDelegateFullNames.length > 0 && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Присутствующие лица</div>
                <div className={styles.ColValue}>
                  {passportData.inspectionErknm.isPresenceDelegateFullNames.map((item, index) => (
                    <div key={index}>{item.value}</div>
                  ))}
                </div>
              </div>
            )}
        </KnmSubtitle>
      )}
      {(passportData.inspectionErknm?.isSelection !== null ||
        passportData.inspectionErknm.selectionReactQuillEditor) && (
        <KnmSubtitle title="Сведения об отборе проб (образцов) продукции (товаров)">
          {passportData.inspectionErknm?.isSelection !== null && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Отметка</div>
              <div className={styles.ColValue}>{passportData.inspectionErknm?.isSelection ? 'Да' : 'Нет'}</div>
            </div>
          )}
          {passportData.inspectionErknm?.selectionReactQuillEditor && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Сведения</div>
              <div className={styles.ColValue}>
                {typeof passportData.inspectionErknm?.selectionReactQuillEditor === 'object' ? (
                  <div
                    /* eslint-disable-next-line react/no-danger */
                    dangerouslySetInnerHTML={{
                      __html: convertQuillDeltaToHtml(
                        passportData.inspectionErknm?.selectionReactQuillEditor as IReactQuillDelta,
                      ),
                    }}
                    className="ql-editor ql-editor-preview-block"
                    style={{ whiteSpace: 'pre-wrap', width: '100%' }}
                  />
                ) : (
                  <>{passportData.inspectionErknm?.selectionReactQuillEditor}</>
                )}
              </div>
            </div>
          )}
        </KnmSubtitle>
      )}
      {(passportData.inspectionErknm?.withVideo !== null ||
        passportData.inspectionErknm.serviceDocument ||
        passportData.inspectionErknm.returnSelection !== null) && (
        <KnmSubtitle title="Иная информация">
          {passportData.inspectionErknm?.withVideo !== null && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Отметка об использовании видеозаписи</div>
              <div className={styles.ColValue}>{passportData.inspectionErknm?.withVideo ? 'Да' : 'Нет'}</div>
            </div>
          )}
          {passportData.inspectionErknm?.serviceDocument && (
            <div className={styles.Row}>
              <div className={styles.ColText}>
                Информация о результате экспертизы продукции (товаров), результатов выполненных работ, оказанных услуг
              </div>
              <div className={styles.ColValue}>
                {passportData.inspectionErknm?.serviceDocument?.descriptionReactQuillEditor &&
                typeof passportData.inspectionErknm?.serviceDocument?.descriptionReactQuillEditor === 'object' ? (
                  <div
                    /* eslint-disable-next-line react/no-danger */
                    dangerouslySetInnerHTML={{
                      __html: convertQuillDeltaToHtml(
                        passportData.inspectionErknm?.serviceDocument?.descriptionReactQuillEditor as IReactQuillDelta,
                      ),
                    }}
                    className="ql-editor ql-editor-preview-block"
                    style={{ whiteSpace: 'pre-wrap', width: '100%' }}
                  />
                ) : (
                  <>{passportData.inspectionErknm?.serviceDocument?.descriptionReactQuillEditor}</>
                )}
              </div>
            </div>
          )}
          {passportData.inspectionErknm?.returnSelection !== null && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Отметка о возврате товара</div>
              <div className={styles.ColValue}>{passportData.inspectionErknm?.returnSelection ? 'Да' : 'Нет'}</div>
            </div>
          )}
        </KnmSubtitle>
      )}
    </div>
  );
});
